import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FeatureName,
  Media,
  MediaType,
  useCurrentWorkspace,
  useCustomQuery,
  useFilteredWorkspaceRoutines,
  useGrippContext,
  useIsFeatureEnabled,
  useProduct,
  useUpdateMutation,
} from '@gripp/shared-logic';
import {
  CategoryFilter,
  Colors,
  ControlledTextInput,
  RoutineButtonData,
  Spacing,
  useAutoFocusRef,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { formStyles } from 'libs/shared-ui/src/styles/forms';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { Button, Portal } from 'react-native-paper';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { AddFilesLinksButton } from './addFilesLinksButton';
import { AssignedProductList } from './assignedProductList';
import { DeleteProduct } from './deleteProduct';
import { EditProductRoutines } from './editProductRoutines';
import { ProductAddOns } from './ProductAddOns';
import { ProductDetailImage } from './productDetailImage';
import { ProductFileItem } from './productFileItem';
import { ProductSpecifications } from './productSpecifications';

type ProductDetailProps = {
  productId: string;
  setColumnTitle?: (title: string | undefined) => void;
  onFilePress?: (media: Media, allImages: Media[]) => void;
};

export const ProductDetail = ({
  productId,
  setColumnTitle,
  onFilePress,
}: ProductDetailProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { product, invalidateQuery, refetch } = useProduct(productId);
  const currentWorkspace = useCurrentWorkspace();
  const { mediaPickerComponent: MediaPicker } = useGrippContext();
  const [newlyUploadedMedia, setNewlyUploadedMedia] = useState<Media | null>(
    null
  );
  const [leftHeight, setLeftHeight] = useState<number | null>(null);
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const location = useLocation();
  const fileSectionRef = useRef<View>(null);

  // Scroll to the files section when URL matches /products/{productId}/files/{fileId}
  useEffect(() => {
    const isFileUrl = /\/products\/[^/]+\/files\/[^/]+/.test(location.pathname);
    if (!isFileUrl || !fileSectionRef.current || !product) return;

    // On React Native Web the ref holds the DOM node directly
    const node = (fileSectionRef.current as unknown as HTMLElement)?.scrollIntoView
      ? (fileSectionRef.current as unknown as HTMLElement)
      : null;

    if (node) {
      // Defer until after layout paint
      const id = setTimeout(() => {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(id);
    }
  }, [location.pathname, product]);

  // Fetch assets count for this product
  const { data: assetsData } = useCustomQuery({
    query: GET_PRODUCT_ASSETS_COUNT,
    variables: {
      filter: {
        product: { eq: productId },
      },
      page: { limit: 1, offset: 0 },
    },
    cacheKey: ['productAssetsCount', productId],
    options: { enabled: !!productId },
  });

  const assetsCount = assetsData?.findAssets?.count || 0;

  // State for category filtering
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >(undefined);

  // Fetch workspace routines
  const workspaceRoutines = useFilteredWorkspaceRoutines(
    {
      workspace: { eq: currentWorkspace?.id },
      deactivated: { ne: true },
    },
    currentWorkspace?.id,
    !currentWorkspace
  );

  // Callback to handle newly uploaded media
  const handleNewMediaUploaded = (media: Media) => {
    setNewlyUploadedMedia(media);
  };

  // Clear newly uploaded media when product media changes (after save)
  useEffect(() => {
    if (product?.media && newlyUploadedMedia) {
      // Check if the newly uploaded media is now in the product media array
      const isNowInProduct = product.media.some(
        (media) => media.id === newlyUploadedMedia.id
      );
      if (isNowInProduct) {
        setNewlyUploadedMedia(null); // Clear it since it's now part of the product
      }
    }
  }, [product?.media, newlyUploadedMedia]);

  const { mutateAsync } = useUpdateMutation({
    modelName: 'product',
    query: UPDATE_PRODUCT,
  });

  const formSchema = yup.object({
    name: yup.string().required(t('product.create.validation.name_required')),
    description: yup.string().optional().nullable(),
    details: yup.string().optional().nullable(),
    specifications: yup.mixed().optional().nullable(),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { isDirty, isValid, errors },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      name: product?.name ?? '',
      description: product?.description ?? '',
      details: product?.details ?? '',
      specifications: (product?.specifications as any) ?? {},
    },
  });

  useEffect(() => {
    if (!product) return;
    reset({
      name: product.name,
      description: product.description,
      details: product.details,
      specifications: (product.specifications as any) || {},
    });
  }, [product, reset]);

  useEffect(() => {
    if (setColumnTitle && product) {
      setColumnTitle(product.name || 'product.list.products');
    }
  }, [product, setColumnTitle]);

  // Get currently assigned routine IDs
  const assignedRoutineIds = useMemo(() => {
    if (!product?.routines || product.routines.length === 0) {
      return [];
    }
    return product.routines
      .filter((r) => r.deactivated !== true)
      .map((routine) => routine.id);
  }, [product?.routines]);

  const getRoutineOrder = useMemo(() => {
    const routineOrderMap = new Map(
      (product?.routineOrder || []).map((ro) => [
        ro.workspaceRoutineId,
        ro.order,
      ])
    );
    return (routineId: string, fallbackOrder: number) =>
      routineOrderMap.get(routineId) ?? fallbackOrder;
  }, [product?.routineOrder]);

  // Product's routines sorted by routineOrder
  const sortedAssignedRoutines = useMemo(() => {
    return [...(product?.routines || [])]
      .filter((r) => !r.deactivated)
      .sort(
        (a, b) =>
          getRoutineOrder(a.id, a.order) - getRoutineOrder(b.id, b.order)
      );
  }, [product?.routines, getRoutineOrder]);

  // Convert workspace routines to RoutineButtonData format with selection state
  const productRoutines: RoutineButtonData[] = useMemo(() => {
    if (!workspaceRoutines || workspaceRoutines.length === 0) {
      return [];
    }

    return workspaceRoutines
      .filter((r) => r.deactivated !== true)
      .sort(
        (a, b) =>
          getRoutineOrder(a.id, a.order) - getRoutineOrder(b.id, b.order)
      )
      .map((routine) => ({
        routineButtonText: routine.config.routineButtonText || '',
        title: routine.config.title,
        routineId: routine.id,
        order: getRoutineOrder(routine.id, routine.order),
        isProductRoutine: assignedRoutineIds.includes(routine.id),
      }));
  }, [workspaceRoutines, assignedRoutineIds, getRoutineOrder]);

  const canSave = isDirty && isValid;

  const elementFocusCallBack = useAutoFocusRef();

  const onSubmit = async (data: any) => {
    const updatedProduct = {
      id: productId,
      name: data.name,
      description: data.description,
      details: data.details,
      specifications: data.specifications,
    };
    await mutateAsync({ input: updatedProduct });
  };

  // Filter files based on selected category
  const filteredFiles = useMemo(() => {
    if (!selectedCategoryId) {
      return product?.media || [];
    }

    return (
      product?.media?.filter((file) =>
        file.categories?.some((category) => category.id === selectedCategoryId)
      ) || []
    );
  }, [product?.media, selectedCategoryId]);

  const renderFiles = useMemo(() => {
    return (
      <View style={styles.fileContainer}>
        <CategoryFilter
          allFiles={product?.media || []}
          selectedCategoryId={selectedCategoryId}
          onCategorySelect={setSelectedCategoryId}
        />
        {filteredFiles.map((file) => (
          <View key={file.id}>
              <ProductFileItem
                product={product}
                media={file}
                invalidateQuery={invalidateQuery}
                onPress={onFilePress ? (m) => {
                  const allImages = (product?.media || []).filter(
                    (f) => f.type === MediaType.Image
                  );
                  onFilePress(m, allImages);
                } : undefined}
                useCropCircle={isCropCircleEnabled}
              />
          </View>
        ))}
      </View>
    );
  }, [
    product?.media, 
    invalidateQuery,
    onFilePress,
    filteredFiles,
    selectedCategoryId,
    isCropCircleEnabled
  ]);

  const renderDeleteAlert = () => {
    const footerContent = (
      <View
        style={[styles.footer, isCropCircleEnabled && styles.footerCropCircle]}
      >
        <DeleteProduct
          product={product}
          onDeleted={() => navigate('/products')}
        />
        <View style={styles.actionButtonGroup}>
          <Button
            mode="text"
            labelStyle={styles.buttonLabel}
            textColor={Colors.orangeDark}
            onPress={() => navigate('..')}
          >
            {t('product.detail.cancel')}
          </Button>
          <Button
            mode="contained"
            style={styles.saveButton}
            disabled={!canSave}
            onPress={handleSubmit(onSubmit)}
          >
            {t('product.detail.save')}
          </Button>
        </View>
      </View>
    );

    if (isCropCircleEnabled) {
      return footerContent;
    }

    return <Portal>{footerContent}</Portal>;
  };

  const renderLeftContainer = () => (
    <View
      style={styles.rightContainer}
      onLayout={(event) => {
        const { height } = event.nativeEvent.layout;
        setLeftHeight(height);
      }}
    >
      <View style={{
        ...isCropCircleEnabled ? styles.card : styles.card_old
      }}>
        {
          !isCropCircleEnabled && (
            <Text style={{
              fontSize: 20,
              fontWeight: 'bold',
              color: Colors.black,
            }}>
              {t('product.detail.profile')}
            </Text>
          )
        }
        <View style={styles.row}>
          <ProductDetailImage
            productCoverImage={product?.coverImage}
            product={product}
            imgSize={100}
            invalidateQuery={invalidateQuery}
            newlyUploadedMedia={newlyUploadedMedia}
            mediaPicker={MediaPicker}
            onImageChanged={() => {
              // Mark the form as dirty when image is changed
              setValue('name', watch('name'), { shouldDirty: true });
            }}
          />
          <View style={styles.column}>
            <View>
              <Text style={formStyles.formItemTitle}>{t('product.list.name')}</Text>
              <ControlledTextInput
                control={control}
                name="name"
                placeholder={t('product.create.placeholder')}
                innerRef={elementFocusCallBack}
                outlinedTextInput
                height={44}
                disableInnerStyle
                useCropCircle={isCropCircleEnabled}
                hideBorder={isCropCircleEnabled}
              />
            </View>
            {
              !isCropCircleEnabled && (
                <View style={styles.assetRow}>
                  <MaterialCommunityIcons name="qrcode" size={16} />
                  <Text>
                    {' '}
                    {assetsCount} {t('product.detail.assets')}
                  </Text>
                </View>
              )
            }
          </View>
        </View>
      </View>
      <View style={{
        ...isCropCircleEnabled ? styles.card : styles.card_old
      }}>
        <View style={styles.row}>
          <Text style={formStyles.formItemTitle}>{t('product.detail.routines')}</Text>
          <EditProductRoutines
            product={product}
            productRoutines={productRoutines}
          />
        </View>
        <AssignedProductList
          productId={product?.id}
          routines={sortedAssignedRoutines}
        />
      </View>
      <View style={{
        ...isCropCircleEnabled ? styles.card : styles.card_old
      }}>
        <Text style={formStyles.formItemTitle}>{t('product.detail.description')}</Text>
        <ControlledTextInput
          control={control}
          name="description"
          placeholder={t('product.create.placeholder')}
          innerRef={elementFocusCallBack}
          textArea
          outlinedTextInput
          useCropCircle={isCropCircleEnabled}
          hideBorder={isCropCircleEnabled}
        />
      </View>
      <View style={{
        ...isCropCircleEnabled ? styles.card : styles.card_old
      }}>
        <Text style={formStyles.formItemTitle}>{t('product.detail.details')}</Text>
        <ControlledTextInput
          control={control}
          name="details"
          placeholder={t('product.create.placeholder')}
          innerRef={elementFocusCallBack}
          textArea
          outlinedTextInput
          useCropCircle={isCropCircleEnabled}
          hideBorder={isCropCircleEnabled}
        />
      </View>
      <ProductSpecifications
        specifications={(watch('specifications') as any) || {}}
        onSpecificationsChange={(newSpecifications) => {
          setValue('specifications', newSpecifications as any, {
            shouldDirty: true,
            shouldValidate: true,
          });
          trigger(); // Trigger validation for all fields
        }}
        useCropCircle={isCropCircleEnabled}
      />
      <ProductAddOns product={product} />
    </View>
  );

  const isEmptyProductFiles = !product?.media || product?.media?.length === 0;

  const renderRightContainer = () => (
    <View
      ref={fileSectionRef}
      style={[
        !isCropCircleEnabled && { width: 400 },
        leftHeight ? { maxHeight: leftHeight } : undefined,
      ]}
    >
      <View style={{
        ...isCropCircleEnabled ? styles.card : styles.card_old, 
        ...{ flex: 1 }
      }}>
        <Text style={formStyles.formItemTitle}>Files</Text>
        <View>
          {isEmptyProductFiles ? (
            <Text style={styles.noMediaLink}>
              {t('product.detail.files.placeholder')}
            </Text>
          ) : (
            <View style={[leftHeight ? { maxHeight: leftHeight - 150 } : null]}>
              <ScrollView showsVerticalScrollIndicator={true}>
                {renderFiles}
              </ScrollView>
            </View>
          )}
          <AddFilesLinksButton
            product={product}
            invalidateQuery={invalidateQuery}
            refetch={refetch}
            onNewMediaUploaded={handleNewMediaUploaded}
          />
        </View>
      </View>
    </View>
  );

  return (
    product !== undefined && (
      <>
        <ScrollView
          contentContainerStyle={styles.scrollView}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
        >
          <View
            style={[
              styles.container,
              isCropCircleEnabled && styles.containerCropCircle,
            ]}
          >
            {renderLeftContainer()}
            {renderRightContainer()}
          </View>
        </ScrollView>
        {renderDeleteAlert()}
      </>
    )
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flexGrow: 1,
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingBottom: 100,
  },
  container: {
    flexDirection: 'row',
    gap: 20,
  },
  containerCropCircle: {
    flexDirection: 'column',
  },
  rightContainer: {
    flex: 1,
    gap: 20,
  },
  card: {
    paddingHorizontal: 20,
    gap: 4,
  },
  card_old: {
    backgroundColor: Colors.white,
    padding: 20,
    borderRadius: 8,
    gap: 20,
  },
  cardHeader: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.black,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  column: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  assetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: Spacing.baseMargin.base,
  },
  saveButton: {
    borderRadius: 12,
  },
  footer: {
    position: 'absolute',
    left: 180,
    right: 0,
    bottom: 0,
    paddingHorizontal: 40,
    paddingVertical: 20,
    backgroundColor: Colors.white,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  footerCropCircle: {
    position: 'relative',
    left: 0,
    marginTop: 20,
    borderRadius: 8,
  },
  buttonLabel: {
    fontSize: 16,
    marginHorizontal: 0,
  },
  actionButtonGroup: {
    flexDirection: 'row',
    gap: 30,
    marginLeft: 'auto',
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 10,
  },
  noMediaLink: {
    marginTop: 10,
    fontSize: 16,
    lineHeight: 16,
    color: Colors.grayText,
  },
  marginTop: {
    marginTop: 150,
  },
  fileContainer: { 
    flexDirection: 'column', 
    gap: 10 
  },
});

const UPDATE_PRODUCT = `
  mutation UpdateProduct($input: MutateProductInput!) {
    updateProduct(input: $input) {
      id
    }
  }
`;

const GET_PRODUCT_ASSETS_COUNT = `
  query GetProductAssetsCount($filter: AssetFilter!, $page: PageRequest) {
    findAssets(filter: $filter, page: $page) {
      count
    }
  }
`;
